import { useCallback, useState, useRef } from 'react'
import { Upload, X, ImageIcon, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Card } from '@/components/ui/card'
import { uploadToS3 } from '@/hooks/useImages'
import { cn } from '@/lib/utils'

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export interface UploadedImage {
  id: string
  file: File
  preview: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
}

export interface ImageUploadProps {
  /** Report ID to upload images to */
  reportId?: string
  /** Maximum number of images allowed */
  maxImages?: number
  /** Callback when images change */
  onChange?: (images: UploadedImage[]) => void
  /** Function to get presigned upload URL */
  getUploadUrl: (contentType: string, filename: string, contentLength: number) => Promise<{ imageId: string; uploadUrl: string }>
  /** Currently uploaded images */
  images: UploadedImage[]
  /** Whether the component is disabled */
  disabled?: boolean
  /** When true, files are added as pending without triggering upload */
  deferUpload?: boolean
}

export function ImageUpload({
  maxImages = 10,
  onChange,
  getUploadUrl,
  images,
  disabled = false,
  deferUpload = false,
}: ImageUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const [rejectedFileError, setRejectedFileError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imagesRef = useRef(images)
  imagesRef.current = images

  const updateImage = useCallback(
    (id: string, updates: Partial<UploadedImage>) => {
      onChange?.(
        imagesRef.current.map((img) => (img.id === id ? { ...img, ...updates } : img))
      )
    },
    [onChange]
  )

  const removeImage = useCallback(
    (id: string) => {
      const image = imagesRef.current.find((img) => img.id === id)
      if (image?.preview) {
        URL.revokeObjectURL(image.preview)
      }
      onChange?.(imagesRef.current.filter((img) => img.id !== id))
    },
    [onChange]
  )

  const uploadImage = useCallback(
    async (image: UploadedImage) => {
      try {
        updateImage(image.id, { status: 'uploading', progress: 0 })

        // Get presigned URL
        const { uploadUrl, imageId } = await getUploadUrl(
          image.file.type,
          image.file.name,
          image.file.size
        )

        // Update with server-assigned image ID
        updateImage(image.id, { id: imageId })

        // Upload to S3
        await uploadToS3(uploadUrl, image.file, (progress) => {
          updateImage(imageId, { progress })
        })

        updateImage(imageId, { status: 'success', progress: 100 })
      } catch (error) {
        updateImage(image.id, {
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        })
      }
    },
    [getUploadUrl, updateImage]
  )

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files)

      // Filter and validate files
      const validFiles: File[] = []
      let oversizeRejected = false
      let typeRejected = false
      for (const file of fileArray) {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          typeRejected = true
          continue
        }
        if (file.size > MAX_FILE_SIZE) {
          oversizeRejected = true
          continue
        }
        validFiles.push(file)
      }

      if (oversizeRejected) {
        setRejectedFileError('File too large — max 10 MB')
      } else if (typeRejected) {
        setRejectedFileError('Unsupported file type')
      } else {
        setRejectedFileError(null)
      }

      // Check max images limit
      const remainingSlots = maxImages - imagesRef.current.length
      const filesToAdd = validFiles.slice(0, remainingSlots)

      if (filesToAdd.length === 0) return

      // Create pending image objects
      const newImages: UploadedImage[] = filesToAdd.map((file) => ({
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview: URL.createObjectURL(file),
        status: 'pending' as const,
        progress: 0,
      }))

      // Add to state
      const updatedImages = [...imagesRef.current, ...newImages]
      onChange?.(updatedImages)

      // Start uploads unless deferred (e.g., report not yet created)
      if (!deferUpload) {
        for (const image of newImages) {
          uploadImage(image)
        }
      }
    },
    [maxImages, onChange, uploadImage, deferUpload]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)

      if (disabled) return

      const files = e.dataTransfer.files
      handleFiles(files)
    },
    [disabled, handleFiles]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!disabled) {
        setIsDragOver(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files) {
        handleFiles(files)
      }
      // Reset input value to allow selecting the same file again
      e.target.value = ''
    },
    [handleFiles]
  )

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }, [disabled])

  const canAddMore = images.length < maxImages

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      {canAddMore && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          className={cn(
            'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer',
            isDragOver
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
          <Upload className="h-8 w-8 text-muted-foreground" />
          <div className="text-center">
            <p className="text-sm font-medium">
              Drag and drop images here, or click to select
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              JPEG, PNG, WebP up to 10MB. Max {maxImages} images.
            </p>
          </div>
        </div>
      )}

      {/* Image previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {images.map((image) => (
            <ImagePreview
              key={image.id}
              image={image}
              onRemove={() => removeImage(image.id)}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {/* Rejected file error */}
      {rejectedFileError && (
        <p className="text-sm text-destructive" role="alert">
          {rejectedFileError}
        </p>
      )}

      {/* Status summary */}
      {images.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {images.length} of {maxImages} images
          {images.some((img) => img.status === 'uploading') && ' (uploading...)'}
          {images.some((img) => img.status === 'error') && (
            <span className="text-destructive"> - Some uploads failed</span>
          )}
        </p>
      )}
    </div>
  )
}

interface ImagePreviewProps {
  image: UploadedImage
  onRemove: () => void
  disabled?: boolean
}

function ImagePreview({ image, onRemove, disabled }: ImagePreviewProps) {
  return (
    <Card className="relative overflow-hidden aspect-square">
      {/* Preview image */}
      {image.preview ? (
        <img
          src={image.preview}
          alt="Upload preview"
          className={cn(
            'w-full h-full object-cover',
            image.status === 'uploading' && 'opacity-50'
          )}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        </div>
      )}

      {/* Upload progress overlay */}
      {image.status === 'uploading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80">
          <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
          <Progress value={image.progress} className="w-3/4 h-2" />
          <span className="text-xs mt-1">{image.progress}%</span>
        </div>
      )}

      {/* Error overlay */}
      {image.status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-destructive/20">
          <AlertCircle className="h-6 w-6 text-destructive mb-1" />
          <span className="text-xs text-destructive text-center px-2">
            {image.error || 'Upload failed'}
          </span>
        </div>
      )}

      {/* Remove button */}
      {!disabled && image.status !== 'uploading' && (
        <Button
          type="button"
          variant="destructive"
          size="icon"
          className="absolute top-1 right-1 h-6 w-6"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </Card>
  )
}
